import type {ShownMoment} from '@Utils/friendly-moment.util';
import {isPlainObject} from '@Utils/object.util';

/**
 * Runtime shape guards for stream-component props. Component payloads are
 * authored by the LLM on the backend, so every registration's parseProps must
 * validate before trusting the shape — a malformed payload is dropped at
 * ingestion instead of crashing the chat at render time.
 */

export const isStringArray = (value: unknown): value is string[] => {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
};

export const isOptionalStringArray = (value: unknown): value is string[] | undefined => {
  return value === undefined || isStringArray(value);
};

export const isOptionalString = (value: unknown): value is string | undefined => {
  return value === undefined || typeof value === 'string';
};

export const isOptionalNonEmptyString = (value: unknown): value is string | undefined => {
  return value === undefined || (typeof value === 'string' && value !== '');
};

export const isShownMoment = (value: unknown): value is ShownMoment => {
  return isPlainObject(value) && Number.isInteger(value.at) && typeof value.zone === 'string';
};

export const parseEachOrNull = <T>(values: unknown[], parseOne: (value: Record<string, unknown>) => T | null): T[] | null => {
  const parsed: T[] = [];

  for (const value of values) {
    const item = isPlainObject(value) ? parseOne(value) : null;
    if (item === null) {
      return null;
    }

    parsed.push(item);
  }

  return parsed;
};

/** An absent list stays undefined; a list that is present must parse whole, or the payload is malformed (null). */
export const parseOptionalList = <T>(value: unknown, parseOne: (value: Record<string, unknown>) => T | null): T[] | undefined | null => {
  if (value === undefined) {
    return undefined;
  }

  if (!Array.isArray(value)) {
    return null;
  }

  return parseEachOrNull(value, parseOne);
};
